import { PDFDocument, rgb, degrees, StandardFonts } from 'pdf-lib';

/**
 * Adds a watermark to a PDF document
 * @param {File} file - The original PDF file
 * @param {Object} options - Watermark options
 * @param {string} options.type - 'text' or 'image'
 * @param {string} options.text - Text content (if type is text)
 * @param {File} options.imageFile - Image file (if type is image)
 * @param {number} options.opacity - 0 to 1
 * @param {number} options.rotation - Degrees (e.g. 45)
 * @param {number} options.size - Font size (text) or scale (image)
 * @param {string} options.color - Hex color string (if text)
 * @param {string} options.position - 'center', 'top-left', 'top-right', 'bottom-left', 'bottom-right', 'custom'
 * @param {number} options.x - Custom x position
 * @param {number} options.y - Custom y position
 * @returns {Promise<Blob>} The watermarked PDF blob
 */
export const addWatermark = async (file, options) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();

    // Default options
    const opacity = options.opacity ?? 0.5;
    const rotation = degrees(options.rotation ?? 0);
    const position = options.position ?? 'center';

    let embedImage = null;
    let embedFont = null;

    if (options.type === 'text') {
        embedFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    } else if (options.type === 'image' && options.imageFile) {
        try {
            const imageBuffer = await options.imageFile.arrayBuffer();
            const imageType = options.imageFile.type;

            // Robust embedding: Try PNG, if fail then JPG. ignore mime type labels which can be wrong.
            try {
                // Try as PNG first
                if (imageType.includes('png')) {
                    try {
                        embedImage = await pdfDoc.embedPng(imageBuffer);
                    } catch (pngErr) {
                        embedImage = await pdfDoc.embedJpg(imageBuffer);
                    }
                }
                // Try as JPG
                else {
                    try {
                        embedImage = await pdfDoc.embedJpg(imageBuffer);
                    } catch (jpgErr) {
                        embedImage = await pdfDoc.embedPng(imageBuffer);
                    }
                }
            } catch (finalErr) {
                throw new Error(`Could not embed image. Please ensure it is a valid PNG or JPG file.`);
            }
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    // Process each page
    pages.forEach(page => {
        const { width, height } = page.getSize();
        let drawX, drawY;
        let contentWidth, contentHeight;

        if (options.type === 'text') {
            const text = options.text || 'CONFIDENTIAL';
            const size = options.size || 50;
            const colorHex = options.color || '#FF0000';
            const r = parseInt(colorHex.slice(1, 3), 16) / 255;
            const g = parseInt(colorHex.slice(3, 5), 16) / 255;
            const b = parseInt(colorHex.slice(5, 7), 16) / 255;

            // Calculate text dimensions
            contentWidth = embedFont.widthOfTextAtSize(text, size);
            contentHeight = size;

            // Calculate position
            const coords = calculatePosition(position, width, height, contentWidth, contentHeight, options.x, options.y);
            drawX = coords.x;
            drawY = coords.y;

            page.drawText(text, {
                x: drawX,
                y: drawY,
                size: size,
                font: embedFont,
                color: rgb(r, g, b),
                opacity: opacity,
                rotate: rotation,
            });
        } else if (options.type === 'image' && embedImage) {
            let scale = options.size || 0.5;
            // Safety: If scale is huge (likely from text mode legacy state), force it down
            if (scale > 10) scale = 0.5;

            contentWidth = embedImage.width * scale;
            contentHeight = embedImage.height * scale;

            // Calculate position
            const coords = calculatePosition(position, width, height, contentWidth, contentHeight, options.x, options.y);
            drawX = coords.x;
            drawY = coords.y;

            page.drawImage(embedImage, {
                x: drawX,
                y: drawY,
                width: contentWidth,
                height: contentHeight,
                opacity: opacity,
                rotate: rotation,
            });
        }
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
};

const calculatePosition = (position, pageWidth, pageHeight, contentWidth, contentHeight, customX, customY) => {
    switch (position) {
        case 'top-left':
            return { x: 20, y: pageHeight - contentHeight - 20 };
        case 'top-right':
            return { x: pageWidth - contentWidth - 20, y: pageHeight - contentHeight - 20 };
        case 'bottom-left':
            return { x: 20, y: 20 };
        case 'bottom-right':
            return { x: pageWidth - contentWidth - 20, y: 20 };
        case 'custom':
            return { x: customX || 0, y: customY || 0 };
        case 'center':
        default:
            return {
                x: (pageWidth / 2) - (contentWidth / 2),
                y: (pageHeight / 2) - (contentHeight / 2)
            };
    }
};
